import { Component, Input, Inject, Output, EventEmitter, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { fadeOutAnimation } from '../_animations/fade-out.animation';
import { DOCUMENT } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  selector: 'ngbd-bank-id-modal',
  template: `
    <div class="modal-header">
      <fa-icon [icon]="['fas', 'spinner']" [pulse]="isSpinning" size="2x" [@fadeOutAnimation]="!isSpinning" style="color: #479cbe"></fa-icon>
    </div>
    <div class="modal-body">
      <div class="media">
        <div class="media-left media-middle pr-15 mr-4">
          <fa-icon [icon]="['fac', 'bid']" size="4x"></fa-icon>
        </div>
        <div class="media-body media-middle">
          <p class="media-heading"><span [innerHTML]="('bankid.' + statusDescription) | translate | safehtml"></span><animated-ellipsis [hidden]="!showEllipsis"></animated-ellipsis></p>
          <p class="media-heading" *ngIf="!!showBankIdLink" [innerHTML]="'bankid.installera-bankid-med-lank' | translate | safehtml"></p>
        </div>
      </div>
      <div class="media" *ngIf="showQrCode && qrCode && qrRemaining > 0">
        <div class="media-body qr-code">
          <qrcode-svg [value]="qrCode" errorCorrectionLevel="L" margin="4"></qrcode-svg>
          <div class="text-center mt-2" *ngIf="qrRemaining !== undefined && qrRemaining !== null">
            <small>
              {{ 'bankid.qr-timer-label' | translate }}: <b>{{ qrRemaining }}</b> s
            </small>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button *ngIf="isSpinning && isOnMobile" type="button" class="btn btn-primary btn-modal" (click)="startBankIdClient()">
        {{ 'bankid.button.starta-bankid-pa-denna-enhet' | translate }}
      </button>
      <button
        *ngIf="showQrCode && qrRemaining <= 30 && qrRemaining > 0"
        type="button"
        class="btn btn-secondary btn-modal"
        (click)="extendTimeout()"
        [disabled]="extendTimeoutDisabled">
        {{ 'bankid.qr-extend-timer-label' | translate }}
      </button>
      <button type="button" class="btn btn-danger btn-modal" (click)="activeModal.close()" [disabled]="!hintCode">
        {{ 'button.' + (isSpinning ? 'avbryt' : 'stang') | translate }}
      </button>
    </div>`,
  animations: [
    fadeOutAnimation
  ]
})
export class NgbdBankIdModal implements OnDestroy {
  @Input() public hintCode: string;
  @Input() public status: string;
  @Input() public redirect: string;
  @Input() public autostartToken: string;
  @Input() public isMobileBankId: boolean;
  @Input() public showQrCode: boolean;
  @Output() public extendTimeoutClicked = new EventEmitter<void>();

  private qrCodeSvg: string;
  public showEllipsis: boolean;
  public showBankIdLink: boolean;
  public extendTimeoutDisabled = false;

  @Input('qrCode')
  get qrCode(): string {
    return this.qrCodeSvg;
  }
  set qrCode(value: string) {
    this.qrCodeSvg = value || '';
  }

  @Input() public set qrRemaining(value: number) {
    this._qrRemaining = value;
    if (this.showQrCode && value > 30) {
      this.extendTimeoutDisabled = false;
    }
  }
  public get qrRemaining(): number {
    return this._qrRemaining;
  }
  private _qrRemaining: number;

  public get isOnMobile(): boolean { return this.deviceDetector.isMobile(); }

  constructor(
    public activeModal: NgbActiveModal,
    protected deviceDetector: DeviceDetectorService,
    @Inject(DOCUMENT) private readonly document: Document) {
  }

  public get isSpinning(): boolean { return !this.status || this.status === 'pending'; }

  public get statusDescription(): string {
    this.showEllipsis = false;
    this.showBankIdLink = false;
    switch (this.hintCode) {
      case 'outstandingTransaction':
      case 'noClient':
        if (this.isMobileBankId) {
          return this.showQrCode ? 'starta-qr' : 'starta';
        }
        this.showEllipsis = true;
        return 'forsoker-starta';
      case 'started':
        this.showEllipsis = true;
        return 'soker-efter';
      case 'userSign':
        return 'skriv-sakerhetskod';
      case 'expiredTransaction':
        if (this.showQrCode) {
          this.showBankIdLink = true;
        }
        return this.showQrCode ? 'misslyckades-lasa-qr' : 'svarar-inte';
      case 'certificateErr':
        return 'gammalt-sparrat';
      case 'userCancel':
        return 'avbruten-av-anvandaren';
      case 'cancelled':
        return 'avbruten';
      case 'startFailed':
        this.showBankIdLink = true;
        return 'app-verkar-inte-finnas';
      default:
        switch (this.status) {
          case 'failed':
            return 'okant-fel';
          case 'pending':
            this.showEllipsis = true;
            return 'identifiering-pagar';
          default:
            this.showEllipsis = true;
            return 'vanligen-vanta';
        }
    }
  }

  public startBankIdClient() {
    this.document.location.href = `bankid:///?autostarttoken=${this.autostartToken}&redirect=${this.redirect || 'null'}`;
  }

  public extendTimeout() {
    this.extendTimeoutDisabled = true;
    this.extendTimeoutClicked.emit();
  }

  ngOnDestroy() {
    this.extendTimeoutClicked.complete();
  }
}
